import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Printer, Download, Calendar, Edit, Save, Search, Plus, RefreshCw, 
  User, Stethoscope, FlaskConical, Pill, ClipboardList, FileCheck, ChevronDown, ChevronUp,
  Activity, Clock, Building2, Phone, Mail, AlertCircle, Maximize2, Minimize2, Filter, X,
  FileSpreadsheet, DollarSign, Package, Trash2, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { toast } from 'sonner';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';

export default function DischargeSummary() {
  const [summaries, setSummaries] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncingToMedical, setSyncingToMedical] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Advanced filter states
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [conditionFilter, setConditionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Comprehensive data for create/edit
  const [comprehensiveData, setComprehensiveData] = useState(null);
  const [loadingComprehensive, setLoadingComprehensive] = useState(false);
  
  const user = JSON.parse(localStorage.getItem('user'));
  
  // Create form data with new fields
  const [createFormData, setCreateFormData] = useState({
    admission_id: '',
    patient_id: '',
    doctor_id: user?.id || '',
    diagnosis_at_discharge: '',
    treatment_given: '',
    medications_prescribed: '',
    follow_up_instructions: '',
    condition_at_discharge: 'stable',
    diet_instructions: '',
    activity_restrictions: '',
    next_visit_date: '',
    doctor_notes: '',
    clinical_course: '',
    discharge_advice: ''
  });
  
  const [editFormData, setEditFormData] = useState({
    diagnosis_at_discharge: '',
    treatment_given: '',
    medications_prescribed: '',
    follow_up_instructions: '',
    condition_at_discharge: 'stable',
    diet_instructions: '',
    activity_restrictions: '',
    next_visit_date: '',
    doctor_notes: '',
    clinical_course: '',
    discharge_advice: ''
  });

  // Billing state for discharge
  const [activeTab, setActiveTab] = useState('medical');
  const [billingItems, setBillingItems] = useState([
    { description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }
  ]);
  const [fixedChargeSearch, setFixedChargeSearch] = useState('');
  const [fixedChargeResults, setFixedChargeResults] = useState([]);
  const [showFixedChargeDropdown, setShowFixedChargeDropdown] = useState(false);
  const fixedChargeRef = useRef(null);
  const [billingNotes, setBillingNotes] = useState('');

  // Search fixed charges for billing
  const searchFixedCharges = async (query) => {
    if (!query.trim()) {
      setFixedChargeResults([]);
      return;
    }
    try {
      const response = await api.get(`/fixed-charges/search?q=${encodeURIComponent(query)}&limit=10`);
      setFixedChargeResults(response.data);
    } catch (error) {
      console.error('Failed to search fixed charges');
    }
  };

  // Handle adding a fixed charge to billing
  const handleAddFixedCharge = (charge) => {
    setBillingItems([...billingItems, {
      description: charge.charge_name,
      amount: charge.default_rate,
      quantity: 1,
      tax_percentage: charge.tax_percentage,
      is_fixed_charge: true,
      fixed_charge_id: charge.id,
      category: charge.category
    }]);
    setFixedChargeSearch('');
    setShowFixedChargeDropdown(false);
    setFixedChargeResults([]);
    toast.success(`Added: ${charge.charge_name}`);
  };

  // Handle billing item changes
  const handleBillingItemChange = (index, field, value) => {
    const newItems = [...billingItems];
    if (field === 'amount') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      newItems[index][field] = parseInt(value) || 1;
    } else if (field === 'tax_percentage') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setBillingItems(newItems);
  };

  // Add custom billing item
  const handleAddBillingItem = () => {
    setBillingItems([...billingItems, { description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }]);
  };

  // Remove billing item
  const handleRemoveBillingItem = (index) => {
    if (billingItems.length <= 1) {
      toast.error('At least one billing item is required');
      return;
    }
    setBillingItems(billingItems.filter((_, i) => i !== index));
  };

  // Calculate billing totals
  const billingTotals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    
    billingItems.forEach(item => {
      const itemTotal = (item.amount || 0) * (item.quantity || 1);
      const itemTax = itemTotal * ((item.tax_percentage || 0) / 100);
      subtotal += itemTotal;
      totalTax += itemTax;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: totalTax.toFixed(2),
      total: (subtotal + totalTax).toFixed(2)
    };
  }, [billingItems]);

  // Reset billing when dialog closes
  const resetBillingState = () => {
    setBillingItems([{ description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }]);
    setBillingNotes('');
    setActiveTab('medical');
  };

  // Advanced filtering for summaries
  const filteredSummaries = useMemo(() => {
    let filtered = summaries;
    
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(summary => 
        summary.patient?.full_name?.toLowerCase().includes(query) ||
        summary.patient?.patient_number?.toLowerCase().includes(query) ||
        summary.diagnosis_at_discharge?.toLowerCase().includes(query) ||
        summary.condition_at_discharge?.toLowerCase().includes(query)
      );
    }
    
    // Doctor filter (attending doctor)
    if (doctorFilter && doctorFilter !== 'all') {
      filtered = filtered.filter(summary => summary.doctor_id === doctorFilter);
    }
    
    // Condition at discharge filter
    if (conditionFilter && conditionFilter !== 'all') {
      filtered = filtered.filter(summary => summary.condition_at_discharge === conditionFilter);
    }
    
    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(summary => {
        const summaryDate = new Date(summary.created_at).toISOString().split('T')[0];
        return summaryDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(summary => {
        const summaryDate = new Date(summary.created_at).toISOString().split('T')[0];
        return summaryDate <= dateTo;
      });
    }
    
    return filtered;
  }, [summaries, searchQuery, doctorFilter, conditionFilter, dateFrom, dateTo]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setDoctorFilter('all');
    setConditionFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || doctorFilter !== 'all' || conditionFilter !== 'all' || dateFrom || dateTo;

  // Export handlers
  const handleExportCSV = () => {
    if (filteredSummaries.length === 0) {
      toast.error('No data to export');
      return;
    }
    // Enrich data with doctor names
    const enrichedData = filteredSummaries.map(summary => ({
      ...summary,
      doctor_name: doctors.find(d => d.id === summary.doctor_id)?.full_name || 'N/A'
    }));
    downloadCSV(enrichedData, EXPORT_COLUMNS.dischargeSummaries, 'discharge_summaries_export');
    toast.success(`Exported ${filteredSummaries.length} summary(s) to CSV`);
  };

  const handleExportExcel = () => {
    if (filteredSummaries.length === 0) {
      toast.error('No data to export');
      return;
    }
    const enrichedData = filteredSummaries.map(summary => ({
      ...summary,
      doctor_name: doctors.find(d => d.id === summary.doctor_id)?.full_name || 'N/A'
    }));
    downloadExcel(enrichedData, EXPORT_COLUMNS.dischargeSummaries, 'discharge_summaries_export');
    toast.success(`Exported ${filteredSummaries.length} summary(s) to Excel`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summariesRes, patientsRes, doctorsRes, admissionsRes] = await Promise.all([
        api.get('/discharge-summary'),
        api.get('/patients'),
        api.get('/users/doctors'),
        api.get('/admissions')
      ]);
      
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      setAdmissions(admissionsRes.data);
      
      // Enrich summaries with patient data
      const enrichedSummaries = summariesRes.data.map(summary => ({
        ...summary,
        patient: patientsRes.data.find(p => p.id === summary.patient_id)
      }));
      
      setSummaries(enrichedSummaries);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch comprehensive data when admission is selected
  const fetchComprehensiveData = async (admissionId) => {
    setLoadingComprehensive(true);
    try {
      const response = await api.get(`/discharge-summary/comprehensive/${admissionId}`);
      setComprehensiveData(response.data);
      
      // Auto-populate form with fetched data
      const { admission, prescriptions } = response.data;
      
      // Build medications string from prescriptions
      let medicationsText = '';
      if (prescriptions && prescriptions.length > 0) {
        medicationsText = prescriptions.flatMap(rx => 
          rx.medications?.map(med => 
            `${med.name} ${med.dosage} - ${med.frequency} for ${med.duration}`
          ) || []
        ).join('\n');
      }
      
      setCreateFormData(prev => ({
        ...prev,
        admission_id: admissionId,
        patient_id: admission?.patient_id || '',
        diagnosis_at_discharge: admission?.diagnosis || '',
        medications_prescribed: medicationsText
      }));
      
    } catch (error) {
      toast.error('Failed to fetch admission data');
      setComprehensiveData(null);
    } finally {
      setLoadingComprehensive(false);
    }
  };

  // Get admissions that don't have discharge summaries yet
  const availableAdmissions = useMemo(() => {
    const dischargedAdmissionIds = summaries.map(s => s.admission_id);
    return admissions.filter(a => 
      a.status === 'discharged' && !dischargedAdmissionIds.includes(a.id)
    );
  }, [admissions, summaries]);

  const handleAdmissionChange = (admissionId) => {
    fetchComprehensiveData(admissionId);
  };

  const resetCreateForm = () => {
    setCreateFormData({
      admission_id: '',
      patient_id: '',
      doctor_id: user?.id || '',
      diagnosis_at_discharge: '',
      treatment_given: '',
      medications_prescribed: '',
      follow_up_instructions: '',
      condition_at_discharge: 'stable',
      diet_instructions: '',
      activity_restrictions: '',
      next_visit_date: '',
      doctor_notes: '',
      clinical_course: '',
      discharge_advice: ''
    });
    setComprehensiveData(null);
    resetBillingState();
  };

  const handleCreateSummary = async (e) => {
    e.preventDefault();
    
    if (!createFormData.admission_id) {
      toast.error('Please select an admission');
      return;
    }
    
    // Validate billing items if any have been added
    const validBillingItems = billingItems.filter(item => item.description.trim() && item.amount > 0);
    
    // Check for fixed charges with zero quantity
    const invalidItems = billingItems.filter(
      item => item.is_fixed_charge && item.description.trim() && (!item.quantity || item.quantity <= 0)
    );
    
    if (invalidItems.length > 0) {
      toast.error('Fixed charge items must have a quantity greater than 0');
      setActiveTab('billing');
      return;
    }
    
    try {
      // Create discharge summary
      const summaryResponse = await api.post('/discharge-summary', createFormData);
      
      // If there are valid billing items, create invoice
      if (validBillingItems.length > 0) {
        const formattedItems = validBillingItems.map(item => ({
          description: item.description,
          amount: item.amount * (item.quantity || 1),
          quantity: item.quantity || 1,
          unit_price: item.amount,
          tax_percentage: item.tax_percentage || 0,
          tax_amount: (item.amount * (item.quantity || 1)) * ((item.tax_percentage || 0) / 100),
          is_fixed_charge: item.is_fixed_charge || false,
          fixed_charge_id: item.fixed_charge_id || null,
          category: item.category || null
        }));
        
        await api.post('/billing', {
          patient_id: createFormData.patient_id,
          items: formattedItems,
          notes: billingNotes || `Discharge billing for admission ${comprehensiveData?.admission?.id?.slice(0, 8) || ''}`
        });
        
        toast.success('Discharge summary and billing invoice created successfully!');
      } else {
        toast.success('Discharge summary created successfully!');
      }
      
      setCreateDialogOpen(false);
      resetCreateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discharge summary');
    }
  };

  const handleEditSummary = (summary) => {
    setSelectedSummary(summary);
    setEditFormData({
      diagnosis_at_discharge: summary.diagnosis_at_discharge || '',
      treatment_given: summary.treatment_given || '',
      medications_prescribed: summary.medications_prescribed || '',
      follow_up_instructions: summary.follow_up_instructions || '',
      condition_at_discharge: summary.condition_at_discharge || 'stable',
      diet_instructions: summary.diet_instructions || '',
      activity_restrictions: summary.activity_restrictions || '',
      next_visit_date: summary.next_visit_date || '',
      doctor_notes: summary.doctor_notes || '',
      clinical_course: summary.clinical_course || '',
      discharge_advice: summary.discharge_advice || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateSummary = async (e) => {
    e.preventDefault();
    if (!selectedSummary) return;
    
    try {
      await api.put(`/discharge-summary/${selectedSummary.id}`, editFormData);
      toast.success('Discharge summary updated successfully!');
      setEditDialogOpen(false);
      setSelectedSummary(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update discharge summary');
    }
  };

  const handleSyncToMedicalRecords = async (summaryId) => {
    setSyncingToMedical(true);
    try {
      const response = await api.post(`/discharge-summary/${summaryId}/sync-to-medical-records`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync to medical records');
    } finally {
      setSyncingToMedical(false);
    }
  };

  const handlePrint = (summary) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    const patient = patients.find(p => p.id === summary.patient_id);
    const doctor = doctors.find(d => d.id === summary.doctor_id);
    const admission = admissions.find(a => a.id === summary.admission_id);
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Discharge Summary - ${patient?.full_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { color: #4A238F; border-bottom: 2px solid #4A238F; padding-bottom: 10px; }
            .header { margin-bottom: 30px; }
            .section { margin-bottom: 20px; }
            .label { font-weight: bold; color: #475569; }
            .value { margin-top: 5px; }
          </style>
        </head>
        <body>
          <h1>Zesta Healthcare - Discharge Summary</h1>
          <div class="header">
            <p><span class="label">Patient Name:</span> ${patient?.full_name}</p>
            <p><span class="label">Patient Number:</span> ${patient?.patient_number}</p>
            <p><span class="label">Discharge Date:</span> ${new Date(summary.discharge_date).toLocaleDateString()}</p>
            <p><span class="label">Doctor:</span> Dr. ${doctor?.full_name}</p>
          </div>
          <div class="section">
            <p class="label">Diagnosis at Discharge:</p>
            <p class="value">${summary.diagnosis_at_discharge}</p>
          </div>
          ${summary.clinical_course ? `<div class="section"><p class="label">Clinical Course:</p><p class="value">${summary.clinical_course}</p></div>` : ''}
          <div class="section">
            <p class="label">Treatment Given:</p>
            <p class="value">${summary.treatment_given}</p>
          </div>
          <div class="section">
            <p class="label">Medications Prescribed:</p>
            <p class="value">${summary.medications_prescribed}</p>
          </div>
          ${summary.discharge_advice ? `<div class="section"><p class="label">Discharge Advice:</p><p class="value">${summary.discharge_advice}</p></div>` : ''}
          <div class="section">
            <p class="label">Follow-up Instructions:</p>
            <p class="value">${summary.follow_up_instructions}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = async (summary) => {
    try {
      const patient = patients.find(p => p.id === summary.patient_id);
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${BACKEND_URL}/api/discharge-summary/${summary.id}/pdf`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to download PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `discharge-summary-${patient?.full_name?.replace(/\s+/g, '-')}-${new Date(summary.discharge_date).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Print combined discharge summary + billing invoice
  const handlePrintInvoice = async (summary) => {
    try {
      const patient = patients.find(p => p.id === summary.patient_id);
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      toast.info('Generating combined discharge & billing PDF...');

      const response = await fetch(`${BACKEND_URL}/api/discharge-summary/${summary.id}/invoice-pdf`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate invoice PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `discharge-invoice-${patient?.full_name?.replace(/\s+/g, '-')}-${new Date(summary.discharge_date).toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Discharge & Invoice PDF downloaded successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to generate invoice PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="discharge-summary-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Discharge Summaries
            </h1>
            <p className="text-slate-600 mt-2">Comprehensive patient discharge documentation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (!open) {
                resetCreateForm();
                setIsMaximized(false);
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-discharge-summary-btn" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Summary
                </Button>
              </DialogTrigger>
              <DialogContent 
                className={`overflow-y-auto transition-all duration-200 ${
                  isMaximized 
                    ? '!fixed !inset-2 !left-2 !top-2 !right-2 !bottom-2 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-16px)] !h-[calc(100vh-16px)] !max-h-none' 
                    : 'max-w-4xl max-h-[90vh]'
                }`}
              >
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#4A238F]" />
                      Create Comprehensive Discharge Summary
                    </DialogTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMaximized(!isMaximized)}
                      className="h-8 w-8 p-0 hover:bg-slate-100"
                      data-testid="maximize-dialog-btn"
                      title={isMaximized ? "Minimize" : "Maximize"}
                    >
                      {isMaximized ? (
                        <Minimize2 className="h-4 w-4" />
                      ) : (
                        <Maximize2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </DialogHeader>
                
                <form onSubmit={handleCreateSummary} className="space-y-6">
                  {/* Admission Selection - Always visible */}
                  <Card className="border-slate-200">
                    <CardHeader className="py-3 bg-slate-50">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Select Admission
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {availableAdmissions.length > 0 ? (
                        <Select 
                          value={createFormData.admission_id} 
                          onValueChange={handleAdmissionChange}
                        >
                          <SelectTrigger data-testid="admission-select">
                            <SelectValue placeholder="Select a discharged admission" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableAdmissions.map((admission) => {
                              const patient = patients.find(p => p.id === admission.patient_id);
                              return (
                                <SelectItem key={admission.id} value={admission.id}>
                                  {patient?.full_name} - Room {admission.room_number} ({admission.diagnosis})
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          No discharged admissions available. Discharge a patient from the Admissions page first.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tabs for Medical Summary and Billing */}
                  {comprehensiveData && (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2 mb-4 bg-slate-200 p-1">
                        <TabsTrigger value="medical" className="flex items-center gap-2 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="medical-tab">
                          <FileText className="h-4 w-4" />
                          Medical Summary
                        </TabsTrigger>
                        <TabsTrigger value="billing" className="flex items-center gap-2 text-slate-700 data-[state=active]:bg-white data-[state=active]:text-[#4A238F] data-[state=active]:shadow-sm" data-testid="billing-tab">
                          <Receipt className="h-4 w-4" />
                          Billing
                          {billingItems.some(item => item.description.trim()) && (
                            <Badge className="ml-1 bg-purple-100 text-purple-700 text-[10px]">
                              {billingItems.filter(item => item.description.trim()).length}
                            </Badge>
                          )}
                        </TabsTrigger>
                      </TabsList>

                      {/* Medical Summary Tab */}
                      <TabsContent value="medical" className="space-y-6 mt-0">
                        {/* Patient & Admission Info */}
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-purple-100">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Patient & Visit Information (Auto-populated)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-slate-500">Patient Name</p>
                                <p className="font-medium">{comprehensiveData.patient?.full_name || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Patient ID</p>
                                <p className="font-medium">{comprehensiveData.patient?.patient_number || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Admission Date</p>
                                <p className="font-medium">{comprehensiveData.admission?.admission_date?.slice(0, 10) || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-slate-500">Room Number</p>
                                <p className="font-medium">{comprehensiveData.admission?.room_number || 'N/A'}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                      {/* Chief Complaints */}
                      {comprehensiveData.admission?.chief_complaint && (
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-amber-50">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <ClipboardList className="h-4 w-4" />
                              Chief Complaints (from Admission)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <p className="text-slate-700">{comprehensiveData.admission.chief_complaint}</p>
                          </CardContent>
                        </Card>
                      )}

                      {/* Lab Results */}
                      {comprehensiveData.lab_entries?.length > 0 && (
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-emerald-50">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <FlaskConical className="h-4 w-4" />
                              Lab Results (During Admission Period)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {comprehensiveData.lab_entries.slice(0, 5).map((lab, idx) => (
                                <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                                  <span className="font-medium">{lab.test_name}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={lab.status === 'completed' ? 'default' : 'outline'}>
                                      {lab.status}
                                    </Badge>
                                    <span className="text-slate-500">{lab.ordered_date?.slice(0, 10)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* AI Scribe Notes */}
                      {comprehensiveData.scribe_sessions?.length > 0 && (
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-violet-50">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <Stethoscope className="h-4 w-4" />
                              AI Scribe Notes (SOAP)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="space-y-3 max-h-48 overflow-y-auto">
                              {comprehensiveData.scribe_sessions.slice(0, 2).map((session, idx) => (
                                <div key={idx} className="p-3 bg-slate-50 rounded text-sm space-y-2">
                                  <p className="text-xs text-slate-500">Session: {session.created_at?.slice(0, 10)}</p>
                                  {session.soap_note?.subjective && (
                                    <p><span className="font-medium">S:</span> {session.soap_note.subjective}</p>
                                  )}
                                  {session.soap_note?.objective && (
                                    <p><span className="font-medium">O:</span> {session.soap_note.objective}</p>
                                  )}
                                  {session.soap_note?.assessment && (
                                    <p><span className="font-medium">A:</span> {session.soap_note.assessment}</p>
                                  )}
                                  {session.soap_note?.plan && (
                                    <p><span className="font-medium">P:</span> {session.soap_note.plan}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Prescriptions */}
                      {comprehensiveData.prescriptions?.length > 0 && (
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-rose-50">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2">
                              <Pill className="h-4 w-4" />
                              Discharge Medications (from Prescriptions)
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4">
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {comprehensiveData.prescriptions.flatMap((rx, rxIdx) => 
                                rx.medications?.map((med, medIdx) => (
                                  <div key={`${rxIdx}-${medIdx}`} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                                    <span className="font-medium">{med.name}</span>
                                    <span className="text-slate-600">{med.dosage} - {med.frequency} ({med.duration})</span>
                                  </div>
                                ))
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                  {/* Doctor Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="doctor_id">Attending Doctor *</Label>
                    <Select 
                      value={createFormData.doctor_id} 
                      onValueChange={(value) => setCreateFormData({...createFormData, doctor_id: value})}
                    >
                      <SelectTrigger data-testid="doctor-select">
                        <SelectValue placeholder="Select doctor" />
                      </SelectTrigger>
                      <SelectContent>
                        {doctors.map((doctor) => (
                          <SelectItem key={doctor.id} value={doctor.id}>
                            Dr. {doctor.full_name} {doctor.specialization && `(${doctor.specialization})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Diagnosis */}
                  <div className="space-y-2">
                    <Label htmlFor="diagnosis">Diagnosis at Discharge *</Label>
                    <Input
                      id="diagnosis"
                      value={createFormData.diagnosis_at_discharge}
                      onChange={(e) => setCreateFormData({...createFormData, diagnosis_at_discharge: e.target.value})}
                      placeholder="Final diagnosis"
                      required
                    />
                  </div>

                  {/* Clinical Course - NEW */}
                  <div className="space-y-2">
                    <Label htmlFor="clinical_course" className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Clinical Course *
                    </Label>
                    <textarea
                      id="clinical_course"
                      value={createFormData.clinical_course}
                      onChange={(e) => setCreateFormData({...createFormData, clinical_course: e.target.value})}
                      className="w-full min-h-[120px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Describe the patient's clinical course during admission, including key events, interventions, and response to treatment..."
                      required
                    />
                  </div>

                  {/* Treatment Given */}
                  <div className="space-y-2">
                    <Label htmlFor="treatment">Treatment Given *</Label>
                    <textarea
                      id="treatment"
                      value={createFormData.treatment_given}
                      onChange={(e) => setCreateFormData({...createFormData, treatment_given: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Describe treatment provided..."
                      required
                    />
                  </div>

                  {/* Medications */}
                  <div className="space-y-2">
                    <Label htmlFor="medications">Discharge Medications *</Label>
                    <textarea
                      id="medications"
                      value={createFormData.medications_prescribed}
                      onChange={(e) => setCreateFormData({...createFormData, medications_prescribed: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="List medications (auto-populated from prescriptions if available)..."
                      required
                    />
                  </div>

                  {/* Discharge Advice - NEW */}
                  <div className="space-y-2">
                    <Label htmlFor="discharge_advice" className="flex items-center gap-2">
                      <FileCheck className="h-4 w-4" />
                      Discharge Advice *
                    </Label>
                    <textarea
                      id="discharge_advice"
                      value={createFormData.discharge_advice}
                      onChange={(e) => setCreateFormData({...createFormData, discharge_advice: e.target.value})}
                      className="w-full min-h-[120px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Provide discharge advice including warning signs to watch for, when to seek immediate medical attention, lifestyle modifications..."
                      required
                    />
                  </div>

                  {/* Follow-up Instructions */}
                  <div className="space-y-2">
                    <Label htmlFor="followup">Follow-up Instructions *</Label>
                    <textarea
                      id="followup"
                      value={createFormData.follow_up_instructions}
                      onChange={(e) => setCreateFormData({...createFormData, follow_up_instructions: e.target.value})}
                      className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Follow-up care instructions..."
                      required
                    />
                  </div>

                  {/* Condition & Next Visit */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Condition at Discharge *</Label>
                      <Select
                        value={createFormData.condition_at_discharge}
                        onValueChange={(value) => setCreateFormData({...createFormData, condition_at_discharge: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="improved">Improved</SelectItem>
                          <SelectItem value="stable">Stable</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Next Visit Date</Label>
                      <Input
                        type="date"
                        value={createFormData.next_visit_date}
                        onChange={(e) => setCreateFormData({...createFormData, next_visit_date: e.target.value})}
                      />
                    </div>
                  </div>

                  {/* Diet & Activity */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Diet Instructions</Label>
                      <Input
                        value={createFormData.diet_instructions}
                        onChange={(e) => setCreateFormData({...createFormData, diet_instructions: e.target.value})}
                        placeholder="e.g., Low sodium diet"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Activity Restrictions</Label>
                      <Input
                        value={createFormData.activity_restrictions}
                        onChange={(e) => setCreateFormData({...createFormData, activity_restrictions: e.target.value})}
                        placeholder="e.g., Bed rest for 2 weeks"
                      />
                    </div>
                  </div>

                  {/* Doctor's Notes */}
                  <div className="space-y-2">
                    <Label>Doctor's Notes</Label>
                    <textarea
                      value={createFormData.doctor_notes}
                      onChange={(e) => setCreateFormData({...createFormData, doctor_notes: e.target.value})}
                      className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                      placeholder="Additional observations..."
                    />
                  </div>
                      </TabsContent>

                      {/* Billing Tab */}
                      <TabsContent value="billing" className="space-y-4 mt-0">
                        {/* Add Fixed Charge from Master */}
                        <Card className="border-purple-200 bg-purple-50">
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-purple-800">
                              <Package className="h-4 w-4" />
                              Add Fixed Charge from Master
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="relative" ref={fixedChargeRef}>
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                              <Input
                                placeholder="Search fixed charges (e.g., Room Rent, Nursing Care, ICU)..."
                                value={fixedChargeSearch}
                                onChange={(e) => {
                                  setFixedChargeSearch(e.target.value);
                                  searchFixedCharges(e.target.value);
                                  setShowFixedChargeDropdown(true);
                                }}
                                onFocus={() => setShowFixedChargeDropdown(fixedChargeResults.length > 0)}
                                className="pl-10 bg-white"
                                data-testid="discharge-fixed-charge-search"
                              />
                              {showFixedChargeDropdown && fixedChargeResults.length > 0 && (
                                <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-y-auto">
                                  {fixedChargeResults.map((charge) => (
                                    <button
                                      key={charge.id}
                                      type="button"
                                      className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b last:border-b-0 flex justify-between items-center"
                                      onClick={() => handleAddFixedCharge(charge)}
                                      data-testid={`discharge-fixed-charge-option-${charge.id}`}
                                    >
                                      <div>
                                        <p className="font-medium text-slate-900">{charge.charge_name}</p>
                                        <p className="text-xs text-slate-500">
                                          <Badge variant="outline" className="mr-2 text-[10px]">{charge.category}</Badge>
                                          Tax: {charge.tax_percentage}%
                                        </p>
                                      </div>
                                      <span className="font-mono font-bold text-purple-700">
                                        ₹{charge.default_rate.toFixed(2)}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>

                        {/* Billing Items List */}
                        <Card className="border-slate-200">
                          <CardHeader className="py-3 bg-slate-50">
                            <CardTitle className="text-sm font-semibold flex items-center justify-between">
                              <span className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4" />
                                Billing Items
                              </span>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm"
                                onClick={handleAddBillingItem}
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Custom Item
                              </Button>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-4 space-y-3">
                            {billingItems.map((item, index) => (
                              <div key={index} className={`p-3 rounded-lg border ${item.is_fixed_charge ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  {item.is_fixed_charge && (
                                    <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                                      <Package className="h-3 w-3 mr-1" />
                                      Fixed Charge
                                    </Badge>
                                  )}
                                  {item.category && (
                                    <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                                  )}
                                  <div className="ml-auto">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemoveBillingItem(index)}
                                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-12 gap-3">
                                  <div className="col-span-5">
                                    <Label className="text-xs text-slate-500">Description</Label>
                                    <Input
                                      placeholder="Item description"
                                      value={item.description}
                                      onChange={(e) => handleBillingItemChange(index, 'description', e.target.value)}
                                      readOnly={item.is_fixed_charge}
                                      className={item.is_fixed_charge ? 'bg-white/50' : ''}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-slate-500">Rate (₹)</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0.00"
                                      value={item.amount}
                                      onChange={(e) => handleBillingItemChange(index, 'amount', e.target.value)}
                                      readOnly={item.is_fixed_charge}
                                      className={item.is_fixed_charge ? 'bg-white/50 font-mono' : 'font-mono'}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-slate-500">Qty</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="1"
                                      value={item.quantity}
                                      onChange={(e) => handleBillingItemChange(index, 'quantity', e.target.value)}
                                      data-testid={`discharge-billing-qty-${index}`}
                                    />
                                  </div>
                                  <div className="col-span-1">
                                    <Label className="text-xs text-slate-500">Tax%</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max="100"
                                      placeholder="0"
                                      value={item.tax_percentage}
                                      onChange={(e) => handleBillingItemChange(index, 'tax_percentage', e.target.value)}
                                      readOnly={item.is_fixed_charge}
                                      className={item.is_fixed_charge ? 'bg-white/50' : ''}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <Label className="text-xs text-slate-500">Total</Label>
                                    <div className="h-10 px-3 py-2 bg-slate-100 rounded-md font-mono text-sm flex items-center justify-end">
                                      ₹{((item.amount || 0) * (item.quantity || 1) * (1 + (item.tax_percentage || 0) / 100)).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>

                        {/* Billing Summary */}
                        <Card className="border-slate-200">
                          <CardContent className="p-4">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-600">Subtotal:</span>
                                <span className="font-mono">₹{billingTotals.subtotal}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-600">Tax:</span>
                                <span className="font-mono">₹{billingTotals.tax}</span>
                              </div>
                              <div className="flex justify-between border-t pt-2 font-bold">
                                <span>Grand Total:</span>
                                <span className="font-mono text-lg text-purple-700">₹{billingTotals.total}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Billing Notes */}
                        <div className="space-y-2">
                          <Label>Billing Notes</Label>
                          <textarea
                            value={billingNotes}
                            onChange={(e) => setBillingNotes(e.target.value)}
                            className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                            placeholder="Additional billing notes..."
                          />
                        </div>
                      </TabsContent>
                    </Tabs>
                  )}

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                      disabled={!createFormData.admission_id || loadingComprehensive}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {billingItems.some(item => item.description.trim() && item.amount > 0) 
                        ? 'Create Summary & Invoice' 
                        : 'Create Summary'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            {/* Search Box */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by Patient name, ID, or Diagnosis..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
                data-testid="discharge-search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>

              {/* Doctor Filter */}
              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className="w-[180px]" data-testid="discharge-doctor-filter">
                  <SelectValue placeholder="Attending Doctor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Condition at Discharge Filter */}
              <Select value={conditionFilter} onValueChange={setConditionFilter}>
                <SelectTrigger className="w-[160px]" data-testid="discharge-condition-filter">
                  <SelectValue placeholder="Condition" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Conditions</SelectItem>
                  <SelectItem value="improved">Improved</SelectItem>
                  <SelectItem value="stable">Stable</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>

              {/* Date From */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  data-testid="discharge-date-from"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1">
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  data-testid="discharge-date-to"
                />
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              {/* Export Buttons */}
              <div className="ml-auto flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-3 text-sm text-slate-500">
              Showing {filteredSummaries.length} discharge summary(s)
              {hasActiveFilters && ' (filtered)'}
            </div>
          </CardContent>
        </Card>

        {/* Summaries List */}
        {filteredSummaries.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchQuery ? `No discharge summaries found matching "${searchQuery}"` : 'No discharge summaries found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredSummaries.map((summary) => {
              const patient = summary.patient;
              const doctor = doctors.find(d => d.id === summary.doctor_id);
              const admission = admissions.find(a => a.id === summary.admission_id);
              
              return (
                <motion.div
                  key={summary.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card 
                    className="border border-slate-200 shadow-sm hover:shadow-md transition-all bg-white cursor-pointer"
                    onClick={() => setSelectedSummary(selectedSummary?.id === summary.id ? null : summary)}
                    data-testid={`discharge-summary-card-${summary.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4">
                          <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-6 w-6 text-[#4A238F]" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900">{patient?.full_name}</h3>
                            <p className="text-sm text-slate-600">{patient?.patient_number}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Discharged: {new Date(summary.discharge_date).toLocaleDateString()}
                              </div>
                              {admission && (
                                <span>Room: {admission.room_number}</span>
                              )}
                              <Badge className={
                                summary.condition_at_discharge === 'improved' ? 'bg-emerald-100 text-emerald-700' :
                                summary.condition_at_discharge === 'stable' ? 'bg-purple-200 text-blue-700' :
                                'bg-amber-100 text-amber-700'
                              }>
                                {summary.condition_at_discharge}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" onClick={() => handlePrint(summary)}>
                            <Printer className="h-4 w-4 mr-1" />
                            Print
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(summary)}>
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handlePrintInvoice(summary)}
                            className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                            data-testid={`print-invoice-btn-${summary.id}`}
                          >
                            <Receipt className="h-4 w-4 mr-1" />
                            Print Invoice
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleEditSummary(summary)}>
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSyncToMedicalRecords(summary.id)}
                            disabled={syncingToMedical}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <FileCheck className="h-4 w-4 mr-1" />
                            Sync to Records
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedSummary(selectedSummary?.id === summary.id ? null : summary)}
                          >
                            {selectedSummary?.id === summary.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {selectedSummary?.id === summary.id && (
                        <div className="mt-6 pt-6 border-t border-slate-200 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Diagnosis at Discharge</p>
                              <p className="text-slate-900">{summary.diagnosis_at_discharge}</p>
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Attending Doctor</p>
                              <p className="text-slate-900">Dr. {doctor?.full_name} {doctor?.specialization && `(${doctor.specialization})`}</p>
                            </div>
                          </div>

                          {summary.clinical_course && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Clinical Course</p>
                              <p className="text-slate-900 bg-slate-50 p-3 rounded">{summary.clinical_course}</p>
                            </div>
                          )}

                          <div>
                            <p className="text-sm font-semibold text-slate-700 mb-1">Treatment Given</p>
                            <p className="text-slate-900">{summary.treatment_given}</p>
                          </div>

                          <div>
                            <p className="text-sm font-semibold text-slate-700 mb-1">Medications Prescribed</p>
                            <p className="text-slate-900 whitespace-pre-line">{summary.medications_prescribed}</p>
                          </div>

                          {summary.discharge_advice && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Discharge Advice</p>
                              <p className="text-slate-900 bg-slate-50 p-3 rounded">{summary.discharge_advice}</p>
                            </div>
                          )}

                          <div>
                            <p className="text-sm font-semibold text-slate-700 mb-1">Follow-up Instructions</p>
                            <p className="text-slate-900">{summary.follow_up_instructions}</p>
                          </div>

                          {(summary.diet_instructions || summary.activity_restrictions) && (
                            <div className="grid grid-cols-2 gap-4">
                              {summary.diet_instructions && (
                                <div>
                                  <p className="text-sm font-semibold text-slate-700 mb-1">Diet Instructions</p>
                                  <p className="text-slate-900">{summary.diet_instructions}</p>
                                </div>
                              )}
                              {summary.activity_restrictions && (
                                <div>
                                  <p className="text-sm font-semibold text-slate-700 mb-1">Activity Restrictions</p>
                                  <p className="text-slate-900">{summary.activity_restrictions}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {summary.next_visit_date && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Next Visit Date</p>
                              <p className="text-slate-900">{new Date(summary.next_visit_date).toLocaleDateString()}</p>
                            </div>
                          )}

                          {summary.doctor_notes && (
                            <div>
                              <p className="text-sm font-semibold text-slate-700 mb-1">Doctor's Notes</p>
                              <p className="text-slate-900">{summary.doctor_notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Discharge Summary</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateSummary} className="space-y-4">
              <div className="space-y-2">
                <Label>Diagnosis at Discharge *</Label>
                <Input
                  value={editFormData.diagnosis_at_discharge}
                  onChange={(e) => setEditFormData({...editFormData, diagnosis_at_discharge: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Clinical Course</Label>
                <textarea
                  value={editFormData.clinical_course}
                  onChange={(e) => setEditFormData({...editFormData, clinical_course: e.target.value})}
                  className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                  placeholder="Clinical course during admission..."
                />
              </div>

              <div className="space-y-2">
                <Label>Treatment Given *</Label>
                <textarea
                  value={editFormData.treatment_given}
                  onChange={(e) => setEditFormData({...editFormData, treatment_given: e.target.value})}
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Medications Prescribed *</Label>
                <textarea
                  value={editFormData.medications_prescribed}
                  onChange={(e) => setEditFormData({...editFormData, medications_prescribed: e.target.value})}
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Discharge Advice</Label>
                <textarea
                  value={editFormData.discharge_advice}
                  onChange={(e) => setEditFormData({...editFormData, discharge_advice: e.target.value})}
                  className="w-full min-h-[100px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                  placeholder="Discharge advice for the patient..."
                />
              </div>

              <div className="space-y-2">
                <Label>Follow-up Instructions *</Label>
                <textarea
                  value={editFormData.follow_up_instructions}
                  onChange={(e) => setEditFormData({...editFormData, follow_up_instructions: e.target.value})}
                  className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Condition at Discharge</Label>
                  <Select
                    value={editFormData.condition_at_discharge}
                    onValueChange={(value) => setEditFormData({...editFormData, condition_at_discharge: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="improved">Improved</SelectItem>
                      <SelectItem value="stable">Stable</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Next Visit Date</Label>
                  <Input
                    type="date"
                    value={editFormData.next_visit_date}
                    onChange={(e) => setEditFormData({...editFormData, next_visit_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Diet Instructions</Label>
                  <Input
                    value={editFormData.diet_instructions}
                    onChange={(e) => setEditFormData({...editFormData, diet_instructions: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Activity Restrictions</Label>
                  <Input
                    value={editFormData.activity_restrictions}
                    onChange={(e) => setEditFormData({...editFormData, activity_restrictions: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Doctor's Notes</Label>
                <textarea
                  value={editFormData.doctor_notes}
                  onChange={(e) => setEditFormData({...editFormData, doctor_notes: e.target.value})}
                  className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}
